import { booleanAttribute, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, signal } from '@angular/core';
import { CartItemComponentStore } from 'cart-data';
import { LoadingSkeletonDirective2 } from 'common-ui';
import { ElementResizeDirective } from 'common-util';
import { ErrorComponent } from 'error-ui';
import { CallForPriceButtonComponent, ItemComponentBase } from 'products-ui';
import { CartItemQuantitySelectorViewComponent } from './views';

/** Below this container width (in em) the field label drops to its short form. Mirrors the `cart-qty` container tier in the stepper styles. */
const COMPACT_LABEL_EM = 14;

@Component({
  selector: 'ideal-cart-item-quantity-selector',
  imports: [
    CartItemQuantitySelectorViewComponent,
    CallForPriceButtonComponent,
    LoadingSkeletonDirective2,
    ErrorComponent,
    ElementResizeDirective,
  ],
  standalone: true,
  templateUrl: './cart-item-quantity-selector.component.html',
  styleUrl: './cart-item-quantity-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CartItemComponentStore],
})
export class CartItemQuantitySelectorComponent extends ItemComponentBase {
  private readonly _store = inject(CartItemComponentStore);
  private readonly _elm: HTMLElement = inject(ElementRef).nativeElement;
  private _fontSizePx: number | undefined;

  /** Show the "Sold in multiples of…" / "n × m = …" hints under the field. Off in compact row layouts. */
  public readonly sellMultipleHints = input(true, { transform: booleanAttribute });

  protected readonly isLoading = computed(() => this._store.cartItemLoading() || this._store.cartItemPricingLoading());
  protected readonly error = computed(() => this._store.cartItemError() || this._store.cartItemPricingError());
  protected readonly isPriceOnRequest = computed(() => this._store.cartItemPricing().pricingModel.isCallForPricing);
  protected readonly pricing = computed(() => this._store.cartItemPricing());
  protected readonly quantity = computed(() => this._store.cartItemQuantity());
  protected readonly isUpdating = computed(() => this._store.cartItemUpdating());
  protected readonly ready = computed(() => !this.isLoading() && !!this.pricing());
  /** Container too narrow for the full label; the CSS tiers handle everything else. */
  protected readonly compact = signal(false);

  constructor() {
    super();

    effect(() => {
      this._store.selectByItemId(this.itemId());
    });
  }

  protected onQuantityChange(quantity: number) {
    this._store.updateItemQuantity(quantity);
  }

  protected onWidthChange(width: number) {
    this._fontSizePx ??= parseFloat(getComputedStyle(this._elm).fontSize) || 16;
    this.compact.set(width < COMPACT_LABEL_EM * this._fontSizePx);
  }
}
